import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AdminPageHeader from './AdminPageHeader';
import NoAccessPanel from './NoAccessPanel';
import { usePermissions } from '../../context/PermissionContext';
import { getStoredSession } from '../../services/api/sessionApi';
import { STAFF_ROLE_LABELS } from '../../constants/staff';
import { ROUTES } from '../../constants/routes';

/**
 * "Priya Nair · Lab Technician" — name from the stored session, role from the
 * shared label map. No name → no line at all (copy-ratified #14: never
 * "Signed in as Doctor." / "Signed in as · Doctor."); the panel drops an empty line.
 */
function signedInLine(role) {
  const session = getStoredSession();
  const name = session && (session.displayName || session.username);
  if (!name) return '';
  return [name, STAFF_ROLE_LABELS[role] || role].filter(Boolean).join(' · ');
}

/**
 * NoAccessGate — wraps a whole page. Renders `children` only for a member who
 * holds `slug`; otherwise the panel inside the normal app header (never a
 * sign-out, never a request behind it).
 *
 * Waits for permissions: `PermissionContext` starts as `{ role: null,
 * permissions: [] }` and is filled by `ProtectedRoute` from the session, so
 * "nothing yet" is a skeleton — never a flash of the page, never a false panel.
 *
 * `slug` may be a list = any-of (e.g. ABDM_ANY_ABHA).
 *
 * `removedVariant` (optional) — if the member HAD the slug and it disappears
 * while the page is open (admin change picked up by a session refresh), the
 * gate swaps to that variant instead of `deniedVariant`.
 */
export default function NoAccessGate({ slug, deniedVariant, removedVariant, children }) {
  const navigate = useNavigate();
  const { permissions, role, hasPermission } = usePermissions();
  const loaded = role !== null || permissions.length > 0;
  const allowed = loaded && [].concat(slug).some(hasPermission);

  const [hadAccess, setHadAccess] = useState(false);
  useEffect(() => { if (allowed) setHadAccess(true); }, [allowed]);

  if (allowed) return children;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <AdminPageHeader />
      <div className="mx-auto max-w-3xl p-6">
        {loaded ? (
          <NoAccessPanel
            variant={hadAccess && removedVariant ? removedVariant : deniedVariant}
            signedInAs={signedInLine(role)}
            onBack={() => navigate(ROUTES.ORG)}
          />
        ) : (
          <div
            role="status"
            data-testid="access-checking"
            className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4"
          >
            {[40, 75, 60].map((w, i) => (
              <div key={i} className="mb-2.5 h-3 animate-pulse rounded bg-slate-200 dark:bg-slate-700" style={{ width: `${w}%` }} />
            ))}
            <div className="mt-3.5 text-[12.5px] text-slate-500 dark:text-slate-400">Checking your access…</div>
          </div>
        )}
      </div>
    </div>
  );
}
